<div class="user-details-container">
  @if (isLoading()) {
    <div class="loading-container">
      <mat-spinner></mat-spinner>
    </div>
  } @else {
    <div class="page-header">
      <button mat-icon-button (click)="goBack()" class="back-btn" matTooltip="Back to User List">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h1>User Details</h1>
      <div class="user-id-badge">ID: {{ id() }}</div>
    </div>

    @if (access() === 'admin') {
      <mat-card class="section-card">
        <mat-card-header>
          <mat-card-title>Admin Account Status</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p>
            Current status: <strong>{{ status() === 'active' ? 'Active' : 'Inactive — pending activation' }}</strong>
          </p>
          @if (isOwnAccount) {
            <p class="hint">You cannot change your own account's status.</p>
          } @else if (status() === 'active') {
            <button mat-raised-button color="warn" [disabled]="isSavingStatus()" (click)="setStatus('inactive')">
              Deactivate
            </button>
          } @else {
            <button mat-raised-button color="primary" [disabled]="isSavingStatus()" (click)="setStatus('active')">
              Activate
            </button>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- Email Outreach: subscribed or not, and a switch for when they ask -->
    <mat-card class="section-card">
      <mat-card-header>
        <mat-card-title>Email Outreach</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (access() === 'blocked') {
          <p><strong>Blocked</strong> - this user is never emailed, whatever the setting below.</p>
        }
        @if (outreachUnsubscribedAt(); as at) {
          <p>
            <span class="outreach-badge unsubscribed">Unsubscribed</span>
            on {{ at | date: 'd MMM y, HH:mm' }} - not sent Email Outreach.
          </p>
          <button mat-stroked-button color="primary" [disabled]="isSavingOutreach()" (click)="setOutreachSubscribed(true)">
            Re-subscribe
          </button>
        } @else {
          <p><span class="outreach-badge subscribed">Subscribed</span> to Email Outreach.</p>
          <button mat-stroked-button [disabled]="isSavingOutreach()" (click)="setOutreachSubscribed(false)">
            Unsubscribe
          </button>
        }
      </mat-card-content>
    </mat-card>

    <!-- Contact Information -->
    <mat-card class="section-card">
      <mat-card-header>
        <mat-card-title>Contact Information</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (contactForm) {
          <form [formGroup]="contactForm" (ngSubmit)="saveContactInfo()">
            <div class="form-row">
              <mat-form-field appearance="outline" class="form-field">
                <mat-label>First Name</mat-label>
                <input matInput formControlName="firstName" required />
                @if (contactForm.get('firstName')?.hasError('required') && contactForm.get('firstName')?.touched) {
                  <mat-error>First name is required</mat-error>
                }
              </mat-form-field>
  
              <mat-form-field appearance="outline" class="form-field">
                <mat-label>Last Name</mat-label>
                <input matInput formControlName="lastName" required />
                @if (contactForm.get('lastName')?.hasError('required') && contactForm.get('lastName')?.touched) {
                  <mat-error>Last name is required</mat-error>
                }
              </mat-form-field>
            </div>
  
            <div class="form-row">
              <mat-form-field appearance="outline" class="form-field full-width">
                <mat-label>Email</mat-label>
                <input matInput type="email" formControlName="email" required />
                @if (contactForm.get('email')?.hasError('required') && contactForm.get('email')?.touched) {
                  <mat-error>Email is required</mat-error>
                }
                @if (contactForm.get('email')?.hasError('email') && contactForm.get('email')?.touched) {
                  <mat-error>Please enter a valid email</mat-error>
                }
              </mat-form-field>
            </div>
  
            <div class="form-row">
              <fh-form-autocomplete
                [options]="phoneCountryCodeOptions()"
                label="Phone Country Code"
                placeholder="Select country code"
                formControlName="phoneCountryCode"
                [required]="true"
                />

              <mat-form-field appearance="outline" class="form-field">
                <mat-label>Phone Number</mat-label>
                <input matInput formControlName="phoneNumber" required />
                @if (contactForm.get('phoneNumber')?.hasError('required') && contactForm.get('phoneNumber')?.touched) {
                  <mat-error>Phone number is required</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="form-row">
              <fh-form-autocomplete
                [options]="countryOptions()"
                label="Country"
                placeholder="Select country"
                formControlName="iso2"
                />
            </div>

            <div class="form-actions">
              <button 
                mat-raised-button 
                color="primary" 
                type="submit"
                [disabled]="contactForm.invalid || isSaving()"
              >
                @if (isSaving()) {
                  <mat-spinner diameter="20"></mat-spinner>
                } @else {
                  Save Contact Info
                }
              </button>
            </div>
          </form>
        }

        <div class="readonly-info">
          <h3>Additional Information</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Address:</span>
              <span class="value">{{ address() || '—' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Postal Code:</span>
              <span class="value">{{ postalCode() || '—' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Created On:</span>
              <span class="value">{{ created_on() | date: 'medium' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Updated On:</span>
              <span class="value">{{ updated_on() | date: 'medium' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Last Access:</span>
              <span class="value">{{ lastAccess() | date: 'medium' }}</span>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- User Information (was "Quiz Information") -->
    <mat-card class="section-card">
      <mat-card-header>
        <mat-card-title>User Information</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <form [formGroup]="quizForm" (ngSubmit)="saveQuizInfo()">
          <div class="form-row">
            <mat-form-field appearance="outline" class="form-field">
              <!-- "Quiz Answers", not "User Information": the card is now called
                   that, and a field inside it with the same name reads as a
                   mistake. This is what the onboarding quiz collected. -->
              <mat-label>Quiz Answers</mat-label>
              <textarea matInput rows="4" formControlName="userInformation"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Tank Capacity</mat-label>
              <input matInput formControlName="tankCapacity" />
            </mat-form-field>
          </div>

          <div class="form-actions">
            <button 
              mat-raised-button 
              color="primary" 
              type="submit"
              [disabled]="isSaving()"
            >
              @if (isSaving()) {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                Save
              }
            </button>
          </div>
        </form>

        <!--
          Badges: the fishkeeper identity badges shown as coloured circles under
          the shopper's email on their storefront profile. Tick and save.
        -->
        <div class="recent-searches">
          <h4 class="recent-searches-title">
            Badges
            @if (badges().length) {
              <span class="recent-searches-count">{{ badges().length }}</span>
            }
          </h4>
          <div class="badge-grid">
            @for (badge of badgeOptions; track badge.slug) {
              <mat-checkbox
                [checked]="badges().includes(badge.slug)"
                (change)="toggleBadge(badge.slug, $event.checked)"
              >
                <span class="badge-option">
                  <span class="badge-circle" [style.background-color]="badge.color">
                    <mat-icon>{{ badge.icon }}</mat-icon>
                  </span>
                  {{ badge.title }}
                </span>
              </mat-checkbox>
            }
          </div>
          <div class="form-actions">
            <button mat-raised-button color="primary" type="button" (click)="saveBadges()" [disabled]="isSaving()">
              Save badges
            </button>
          </div>
        </div>

        <!--
          Fish Hunt: searches the shopper asked to be notified about from the
          storefront search ("Can't find your fish? Notify me when found"),
          newest first. Read-only, laid out like Recent Searches below.
        -->
        <div class="recent-searches">
          <h4 class="recent-searches-title">
            Fish Hunt
            @if (fishHunts().length) {
              <span class="recent-searches-count">{{ fishHunts().length }}</span>
            }
          </h4>
          @if (fishHunts().length) {
            <div class="recent-searches-scroll">
              <table class="recent-searches-table">
                <thead>
                  <tr>
                    <th scope="col" class="col-index">#</th>
                    <th scope="col" class="col-market">Market</th>
                    <th scope="col" class="col-when">Date &amp; time</th>
                    <th scope="col" class="col-term">Looking for</th>
                  </tr>
                </thead>
                <tbody>
                  @for (hunt of fishHunts(); track $index) {
                    <tr>
                      <td class="col-index">{{ $index + 1 }}</td>
                      <td class="col-market">{{ (hunt.iso2 | uppercase) || '—' }}</td>
                      <td class="col-when">{{ hunt.created_at | date: 'dd MMM yyyy, HH:mm' }}</td>
                      <td class="col-term">{{ hunt.query }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="recent-searches-empty">
              This user hasn't asked to be notified about any fish yet.
            </p>
          }
        </div>

        <!--
          Recent searches: read-only, newest first, capped at the last 50.
          Nothing to save here - it is what the shopper actually typed, and the
          point is to read it beside the quiz answers rather than edit it.
        -->
        <div class="recent-searches">
          <h4 class="recent-searches-title">
            Recent Searches
            @if (recentSearches().length) {
              <span class="recent-searches-count">{{ recentSearches().length }}</span>
            }
          </h4>
          @if (recentSearches().length) {
            <!-- The header stays put while the body scrolls, so the columns are
                 still readable fifty rows down. -->
            <div class="recent-searches-scroll">
              <table class="recent-searches-table">
                <thead>
                  <tr>
                    <th scope="col" class="col-index">#</th>
                    <th scope="col" class="col-market">Market</th>
                    <th scope="col" class="col-when">Date &amp; time</th>
                    <th scope="col" class="col-term">Search term</th>
                  </tr>
                </thead>
                <tbody>
                  @for (search of recentSearches(); track $index) {
                    <tr>
                      <td class="col-index">{{ $index + 1 }}</td>
                      <td class="col-market">{{ (search.iso2 | uppercase) || '—' }}</td>
                      <td class="col-when">{{ search.created_at | date: 'dd MMM yyyy, HH:mm' }}</td>
                      <td class="col-term">{{ search.query }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="recent-searches-empty">
              No searches recorded for this user yet.
            </p>
          }
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Admin Remarks & Follow-up -->
    <mat-card class="section-card">
      <mat-card-header>
        <mat-card-title>Admin Remarks & Follow-up</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <form [formGroup]="remarksForm" (ngSubmit)="saveRemarks()">
          <div class="form-row full-width">
            <mat-form-field appearance="outline" class="form-field full-width">
              <mat-label>Follow-up Notes</mat-label>
              <textarea 
                matInput 
                formControlName="adminFollowup" 
                rows="4"
                placeholder="Add follow-up notes..."
              ></textarea>
            </mat-form-field>
          </div>

          <div class="form-row full-width">
            <mat-form-field appearance="outline" class="form-field full-width">
              <mat-label>Admin Remarks</mat-label>
              <textarea 
                matInput 
                formControlName="adminRemarks" 
                rows="4"
                placeholder="Add admin remarks..."
              ></textarea>
            </mat-form-field>
          </div>

          <div class="form-actions">
            <button 
              mat-raised-button 
              color="primary" 
              type="submit"
              [disabled]="isSaving()"
            >
              @if (isSaving()) {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                Save Remarks
              }
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- Cart: exactly as the customer sees it (built by web-backend). -->
    @if (cart()) {
      <mat-card class="section-card cart-items-card">
        <mat-card-header>
          <mat-card-title>Cart ({{ cart()!.totalItems }} item(s))</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <fh-customer-cart-view [cart]="cart()!"></fh-customer-cart-view>
        </mat-card-content>
      </mat-card>
    } @else {
      <mat-card class="section-card">
        <mat-card-content>
          <p class="empty-cart">No items in cart</p>
        </mat-card-content>
      </mat-card>
    }

    <!-- Order History -->
    <mat-card class="section-card">
      <mat-card-header>
        <mat-card-title>Order History</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (isLoadingOrders()) {
          <div class="loading-container">
            <mat-spinner diameter="36"></mat-spinner>
          </div>
        } @else if (orders().length === 0) {
          <p class="empty-cart">No orders found for this user.</p>
        } @else {
          <div class="order-history-scroll">
            <table mat-table [dataSource]="orders()" class="order-history-table">
              @for (col of orderColumns; track col) {
                <ng-container [matColumnDef]="col">
                  <th mat-header-cell *matHeaderCellDef>{{ orderColumnHeader(col) }}</th>
                  <td mat-cell *matCellDef="let row">{{ formatOrderColumn(col, row[col]) }}</td>
                </ng-container>
              }
              <tr mat-header-row *matHeaderRowDef="orderColumns; sticky: true"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: orderColumns"
                class="order-row"
                (click)="goToOrder(row.id)"
              ></tr>
            </table>
          </div>
        }
      </mat-card-content>
    </mat-card>
  }
</div>

